import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';
import { FilterOperationType, type IFilter } from '@chax-at/prisma-filter-common';
import type { ISchoolSubjectFilter, ISchoolSubjectTenantTemplateFilter } from 'common/interface/filter.interface';

export async function fetchSchoolSubjectsForClass(params: {
  classId: number;
}): Promise<Components.Schemas.SchoolSubjectDto[]> {
  const filter: IFilter<ISchoolSubjectFilter> = {
    filter: [
      { field: 'isTemplate', type: FilterOperationType.Eq, value: false },
      { field: 'schoolClassId', type: FilterOperationType.Eq, value: params.classId },
    ],
    order: [{ field: 'order', dir: 'asc' }],
  };

  const res = await Api.Routes.SchoolSubject.retrieve({ $filter: filter });
  return res.rows;
}

export async function fetchSchoolSubjectsForTemplateSet(params: {
  templateSetId: number;
  schoolLevel: number;
}): Promise<Components.Schemas.SchoolSubjectDto[]> {
  const filter: IFilter<ISchoolSubjectFilter> = {
    filter: [
      { field: 'isTemplate', type: FilterOperationType.Eq, value: true },
      { field: 'schoolLevel', type: FilterOperationType.Eq, value: params.schoolLevel },
      { field: 'templateSetId', type: FilterOperationType.Eq, value: params.templateSetId },
    ],
    order: [{ field: 'order', dir: 'asc' }],
  };

  const res = await Api.Routes.SchoolSubject.retrieveGlobalTemplates({ $filter: filter });
  return res.rows;
}

export async function fetchTenantSubjectTemplates(params?: {
  templateSetId?: number;
  schoolLevel?: number;
}): Promise<Components.Schemas.SchoolSubjectDto[]> {
  const filter: IFilter<ISchoolSubjectTenantTemplateFilter> = {
    order: [{ field: 'order', dir: 'asc' }],
  };

  if (params?.templateSetId) {
    filter.filter = [{ field: 'templateSetId', type: FilterOperationType.Eq, value: params.templateSetId }];
  }

  if (params?.schoolLevel != null) {
    filter.filter = [
      ...(filter.filter ?? []),
      { field: 'schoolLevel', type: FilterOperationType.Eq, value: params.schoolLevel },
    ];
  }

  const res = await Api.Routes.Tenant.retrieveTenantSubjectTemplates({ $filter: filter });
  return res.rows;
}

export async function fetchAvailableSchoolSubjectsForSchoolLevel(params: {
  schoolLevel: number;
}): Promise<Components.Schemas.SchoolSubjectDto[]> {
  const filter: IFilter<ISchoolSubjectFilter> = {
    filter: [
      { field: 'isTemplate', type: FilterOperationType.Eq, value: true },
      { field: 'schoolLevel', type: FilterOperationType.Eq, value: params.schoolLevel },
    ],
    order: [{ field: 'order', dir: 'asc' }],
  };

  const res = await Api.Routes.SchoolSubject.retrieve({ $filter: filter });
  return res.rows;
}

export async function createSchoolSubject(params: {
  schoolClassId: number;
  dto: Components.Schemas.CreateSchoolSubjectDto;
}): Promise<void> {
  await Api.Routes.SchoolSubject.create({ schoolClassId: params.schoolClassId }, params.dto);
}

export async function createSchoolSubjectTemplate(dto: Components.Schemas.CreateSchoolSubjectDto): Promise<void> {
  await Api.Routes.SchoolSubject.createTemplate({}, dto);
}

export async function updateSchoolSubject(params: {
  schoolSubjectId: number;
  dto: Components.Schemas.UpdateSchoolSubjectDto;
}): Promise<void> {
  await Api.Routes.SchoolSubject.update({ schoolSubjectId: params.schoolSubjectId }, params.dto);
}

export async function deleteSchoolSubject(params: { schoolSubjectId: number }): Promise<void> {
  await Api.Routes.SchoolSubject.delete({ schoolSubjectId: params.schoolSubjectId });
}

export async function copySchoolSubjectsFromTemplate(params: {
  schoolClassId: number;
  dto: Components.Schemas.CopySchoolSubjectsDto;
}): Promise<void> {
  await Api.Routes.SchoolSubject.copyFromTemplate({ schoolClassId: params.schoolClassId }, params.dto);
}

export async function duplicateSchoolSubject(params: {
  schoolSubjectId: number;
  dto: Components.Schemas.DuplicateSchoolSubjectDto;
}): Promise<Components.Schemas.SchoolSubjectDto> {
  const response = await Api.Routes.SchoolSubject.duplicate({ schoolSubjectId: params.schoolSubjectId }, params.dto);
  return response.data;
}

export async function importSchoolSubjectsZip(params: { schoolClassId: number; file: File }): Promise<void> {
  await Api.Routes.SchoolSubject.import({ schoolClassId: params.schoolClassId }, { file: params.file });
}

export async function exportSchoolSubjectsZip(params: { idsToExport: number[] }): Promise<string> {
  return Api.Routes.SchoolSubject.export(undefined, { idsToExport: params.idsToExport });
}

export async function getStudentsWithAccessToSubject(params: {
  schoolSubjectId: number;
}): Promise<Components.Schemas.BaseStudentDto[]> {
  const res = await Api.Routes.SchoolSubject.getStudentsWithAccessToSubject({
    schoolSubjectId: params.schoolSubjectId,
  });
  return res.rows;
}

export async function canUserAccessAllSubjectsInClass(params: { classId: number }): Promise<boolean> {
  const res = await Api.Routes.SchoolSubject.canUserAccessAllSubjectsInClass({ schoolClassId: params.classId });
  return res.data;
}

export async function assignStudentsToSubject(params: {
  schoolSubjectId: number;
  dto: Components.Schemas.AssignStudentsToSubjectDto;
}): Promise<void> {
  await Api.Routes.SchoolSubject.assignStudentsToSubject({ schoolSubjectId: params.schoolSubjectId }, params.dto);
}

export async function setUseSubjectSpecificAssessments(params: {
  schoolSubjectId: number;
  dto: Components.Schemas.SetSubjectSpecificAssessmentsDto;
}): Promise<void> {
  await Api.Routes.SchoolSubject.setUseSubjectSpecificAssessments(
    { schoolSubjectId: params.schoolSubjectId },
    params.dto,
  );
}

export async function getSubjectSpecificAssessmentSetupPreview(params: {
  schoolSubjectId: number;
}): Promise<Components.Schemas.SubjectSpecificAssessmentSetupPreviewDto> {
  const res = await Api.Routes.SchoolSubject.getSubjectSpecificAssessmentSetupPreview({
    schoolSubjectId: params.schoolSubjectId,
  });
  return res.data;
}

export async function setupSubjectSpecificAssessments(params: {
  schoolSubjectId: number;
}): Promise<Components.Schemas.SchoolSubjectDto> {
  const res = await Api.Routes.SchoolSubject.setupSubjectSpecificAssessments({
    schoolSubjectId: params.schoolSubjectId,
  });
  return res.data;
}

export async function fetchSchoolSubject(schoolSubjectId: number): Promise<Components.Schemas.SchoolSubjectDto> {
  return (await Api.Routes.SchoolSubject.get({ id: schoolSubjectId })).data;
}
